import axios, { AxiosResponse } from "axios";
import { ReactElement } from "react";
import ErrorCode from "../../common/errorCode";
import { ProblemDetails } from "../../common/problemDetails";
import HttpStatusCode from "../../common/httpStatusCode";
import LocationProvider from "../../common/providers/locationProvider";
import { handledErrorCodes } from "../../utils/errorUtils";
import { useAuth } from "../../context/auth/AuthContext";

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: (...args: Array<unknown>) => void;
}

const ErrorFallback = ({ error }: ErrorFallbackProps): ReactElement => {
  const { authService } = useAuth();
  const locationProvider = new LocationProvider();

  const redirectToLoginPage = (): ReactElement => {
    const redirectUrl = authService.authorize();
    locationProvider.replace(redirectUrl);
    return <></>;
  };

  const redirectToErrorPage = (status: string): ReactElement => {
    locationProvider.replace(`/error/${status}`);
    return <></>;
  };

  let handledResult: ReactElement | null = null;
  if (axios.isAxiosError(error)) {
    const response = error.response as AxiosResponse<ProblemDetails>;
    if (!response) {
      return redirectToErrorPage(ErrorCode.Unknown.toString());
    }

    switch (response.status) {
      case HttpStatusCode.Unauthorized:
        handledResult = redirectToLoginPage();
        break;

      case HttpStatusCode.NotFound:
      case HttpStatusCode.BadGateway:
      case HttpStatusCode.ServiceUnavailable:
        handledResult = redirectToErrorPage(response.status.toString());
        break;

      case HttpStatusCode.Forbidden:
        if (handledErrorCodes.includes(response.data.errorCode)) {
          handledResult = redirectToErrorPage(response.data.errorCode.toString());
        } else {
          handledResult = redirectToErrorPage(response.status.toString());
        }
        break;

      default:
        handledResult = redirectToErrorPage(ErrorCode.Unknown.toString());
        break;
    }
  }

  return handledResult ?? redirectToErrorPage(ErrorCode.Unknown.toString());
};

export default ErrorFallback;
